import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Card,
  CardContent,
} from "@mui/material";
import {
  EventNote as EventNoteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  List as ListIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  Warning as WarningIcon,
  AccessTime as DurationIcon,
  Healing as RecoveryIcon,
  LocalHospital as InterventionIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { QK } from "../../../../../../base/qk";
import queryClient from "../../../../../../base/QueryClient";
import {
  createSeizure,
  deleteSeizureById,
  getSeizure,
} from "../../../../../../api/services/observations";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import EditSeizureModal from "./EditSeizureModal";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomDateField from "../../../../../FormFields/CustomDateField";
import CustomTimePicker from "../../../../../FormFields/TiimePicker/TimePicker";
import DataTable from "../../../../../Table/DataTable";

const getNowDate = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getNowTime = () => {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const SeizureChart = () => {
  const theme = useTheme();
  const { id: clientId } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedSeizure, setSelectedSeizure] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [handover, setHandover] = useState(false);
  const [manager, setManager] = useState(false);
  const [formData, setFormData] = useState({
    date: getNowDate(),
    time: getNowTime(),
    location: "",
    description: "",
    possible_triggers: "",
    warning: "",
    userFeeling: "",
    duringSeizure: "",
    length: "",
    afterSeizure: "",
    recovery: "",
    intervention: "",
    action: "",
    founder: "",
  });

  const { data: seizures, refetch, isLoading } = useQuery(
    [QK.seizure, clientId],
    () => getSeizure(clientId)
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      date: getNowDate(),
      time: getNowTime(),
      location: "",
      description: "",
      possible_triggers: "",
      warning: "",
      userFeeling: "",
      duringSeizure: "",
      length: "",
      afterSeizure: "",
      recovery: "",
      intervention: "",
      action: "",
      founder: "",
    });
    setHandover(false);
    setManager(false);
  };

  const seizurePostMutation = useMutation(createSeizure, {
    onSuccess: () => {
      enqueueSnackbar("Seizure created successfully", { variant: "success" });
      refetch();
      resetForm();
      setActiveTab(0);
    },
    onError: () =>
      enqueueSnackbar("Failed to create Seizure", { variant: "error" }),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const combinedDateTime = formData.date && formData.time
      ? `${formData.date}T${formData.time}:00`
      : "";

    const payload = {
      date_time: combinedDateTime,
      location: formData.location,
      description: formData.description,
      possible_triggers: formData.possible_triggers,
      warning_aura: formData.warning,
      user_feeling: formData.userFeeling,
      during_seizure: formData.duringSeizure,
      seizure_length: formData.length,
      after_seizure: formData.afterSeizure,
      recovery_period: formData.recovery,
      staff_intervention: formData.intervention,
      action_taken: formData.action,
      founder: formData.founder,
      handover,
      manager,
      client: clientId,
    };
    seizurePostMutation.mutate(payload);
  };

  const seizureDeleteMutation = useMutation(deleteSeizureById, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.seizure]);
      enqueueSnackbar("Seizure deleted successfully", { variant: "success" });
      refetch();
      setShowConfirmModal(false);
    },
  });

  const triggerOptions = [
    { value: "Unknown", label: "Unknown" },
    { value: "illness", label: "Illness" },
    { value: "Constipation", label: "Constipation" },
    { value: "Sleep (pattern)", label: "Sleep (pattern)" },
    { value: "Changes in medication", label: "Changes in medication" },
  ];

  const warningOptions = [
    { value: "Unknown", label: "Unknown" },
    { value: "none", label: "None" },
    { value: "Behaviour Changes", label: "Behaviour Changes" },
    { value: "Lethargy", label: "Lethargy" },
    { value: "Mood Change", label: "Mood Change" },
  ];

  const duringOptions = [
    { value: "Unknown", label: "Unknown" },
    { value: "Mumbling (Automatism)", label: "Mumbling (Automatism)" },
    { value: "Laughing (Automatism)", label: "Laughing (Automatism)" },
    {
      value: "Wandering around (Automatism)",
      label: "Wandering around (Automatism)",
    },
    { value: "Confusion (Automatism)", label: "Confusion (Automatism)" },
  ];

  const afterOptions = [
    { value: "Immediate recovery", label: "Immediate recovery" },
    { value: "Confusion", label: "Confusion" },
    { value: "Slept", label: "Slept" },
    { value: "Aggressive", label: "Aggressive" },
    {
      value: "Automatisms (record which)",
      label: "Automatisms (record which)",
    },
  ];

  const interventionOptions = [
    { value: "PRN given (record below)", label: "PRN given (record below)" },
    { value: "Gp visit", label: "Gp visit" },
    { value: "Ambulance called", label: "Ambulance called" },
    { value: "Hospital admission", label: "Hospital admission" },
    { value: "Other (record below)", label: "Other (record below)" },
  ];

  const headers = [
    { key: "date_time", label: "Date & Time" },
    { key: "location", label: "Location" },
    { key: "description", label: "Description" },
    { key: "possible_triggers", label: "Trigger" },
    { key: "during_seizure", label: "During" },
    { key: "after_seizure", label: "After" },
    { key: "seizure_length", label: "Length" },
    { key: "manager", label: "Manager" },
    { key: "handover", label: "Handover" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "seizure_length") {
      return `${item.seizure_length || 0} min`;
    }
    if (header.key === "date_time") {
      return item.date_time ? new Date(item.date_time).toLocaleString() : "-";
    }
    if (header.key === "manager") {
      return item.manager ? (
        <Chip
          label="Yes"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 500,
            bgcolor: "#D1FAE5",
            color: "#039855",
          }}
        />
      ) : (
        <Chip
          label="No"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 500,
            bgcolor: "#FEE2E2",
            color: "#DC2626",
          }}
        />
      );
    }
    if (header.key === "handover") {
      return item.handover ? (
        <Chip
          label="Yes"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 500,
            bgcolor: "#D1FAE5",
            color: "#039855",
          }}
        />
      ) : (
        <Chip
          label="No"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 500,
            bgcolor: "#FEE2E2",
            color: "#DC2626",
          }}
        />
      );
    }
    if (
      header.key === "location" ||
      header.key === "description" ||
      header.key === "possible_triggers" ||
      header.key === "during_seizure" ||
      header.key === "after_seizure"
    ) {
      return (
        <span
          style={{
            display: "block",
            maxWidth: 120,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item[header.key] || "-"}
        </span>
      );
    }
    return item[header.key] || "-";
  };

  const cardSx = {
    p: 1.75,
    borderRadius: "10px",
    border: "1px solid #EAECF0",
    bgcolor: "#FFFFFF",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
    flex: "1 1 220px",
    minWidth: 0,
  };

  const fieldHeaderSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 1.25,
  };

  const iconBoxSx = (color) => ({
    width: 26,
    height: 26,
    borderRadius: "7px",
    bgcolor: alpha(color, 0.1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  });

  const labelSx = {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#344054",
  };

  const requiredBadgeSx = {
    fontSize: "0.65rem",
    fontWeight: 600,
    color: "#F04438",
    ml: "auto",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Card
        sx={{
          width: "100%",
          borderRadius: "14px",
          border: "1px solid #EAECF0",
          boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.04)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            bgcolor: "#FFFFFF",
            borderBottom: "1px solid #EAECF0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "10px",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EventNoteIcon
                  sx={{ color: theme.palette.primary.main, fontSize: 18 }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: "#101828",
                    letterSpacing: "-0.01em",
                  }}
                >
                  Seizure Observation Chart
                </Typography>
                <Typography
                  sx={{ fontSize: "0.7rem", color: "#667085", mt: 0.2 }}
                >
                  Manage seizure observations and history
                </Typography>
              </Box>
            </Box>
            <Chip
              label={`${seizures?.length || 0} records`}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: "#F2F4F7",
                color: "#344054",
                borderRadius: "6px",
                border: "1px solid #EAECF0",
              }}
            />
          </Box>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 40,
              "& .MuiTabs-indicator": {
                backgroundColor: "#2469F0",
                height: 2.5,
                borderRadius: "3px 3px 0 0",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                minHeight: 40,
                color: "#667085",
                px: 2,
                "&.Mui-selected": {
                  color: "#2469F0",
                },
              },
            }}
          >
            <Tab
              icon={<ListIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Records"
              sx={{ gap: 0.5 }}
            />
            <Tab
              icon={<AddIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Add Record"
              sx={{ gap: 0.5 }}
            />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <CardContent sx={{ p: 0 }}>
            <DataTable
              data={seizures || []}
              headers={headers}
              onUpdate={(id) => {
                setSelectedSeizure(id);
                setShowEditModal(true);
              }}
              onDelete={(id) => {
                setSelectedSeizure(id);
                setShowConfirmModal(true);
              }}
              canUpdate={true}
              canDelete={true}
              renderCell={renderCell}
              loading={isLoading}
            />
          </CardContent>
        )}

        {activeTab === 1 && (
          <CardContent sx={{ p: 3 }}>
            <form
              onSubmit={handleSubmit}
              id="seizure-form"
              style={{ width: "100%" }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  width: "100%",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#2469F0", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#2469F0", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#2469F0")}>
                      <CalendarIcon sx={{ color: "#2469F0", fontSize: 16 }} />
                    </Box>
                    <Typography sx={labelSx}>Date</Typography>
                    <Typography sx={requiredBadgeSx}>Required</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomDateField
                      name="date"
                      label=""
                      value={formData.date}
                      onChange={handleChange}
                      required
                      fullWidth
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#8B5CF6", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#8B5CF6", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#8B5CF6")}>
                      <TimeIcon sx={{ color: "#8B5CF6", fontSize: 16 }} />
                    </Box>
                    <Typography sx={labelSx}>Time</Typography>
                    <Typography sx={requiredBadgeSx}>Required</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomTimePicker
                      name="time"
                      label=""
                      value={formData.time}
                      onChange={handleChange}
                      required
                      fullWidth
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#8B5CF6", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#8B5CF6", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#8B5CF6")}>
                      <LocationIcon sx={{ color: "#8B5CF6", fontSize: 16 }} />
                    </Box>
                    <Typography sx={labelSx}>Location</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomInput
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="e.g. Living Room"
                      fullWidth
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#F59E0B", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#F59E0B", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#F59E0B")}>
                      <DescriptionIcon
                        sx={{ color: "#F59E0B", fontSize: 16 }}
                      />
                    </Box>
                    <Typography sx={labelSx}>Description of Seizure</Typography>
                    <Typography sx={requiredBadgeSx}>Required</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomInput
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      fullWidth
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#2469F0", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#2469F0", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#2469F0")}>
                      <WarningIcon sx={{ color: "#2469F0", fontSize: 16 }} />
                    </Box>
                    <Typography sx={labelSx}>Possible Triggers</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomSelect
                      name="possible_triggers"
                      options={triggerOptions}
                      value={formData.possible_triggers}
                      onChange={handleChange}
                      placeholder="Select"
                      fullWidth
                      searchable
                      searchPlaceholder="Search trigger..."
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#F59E0B", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#F59E0B", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#F59E0B")}>
                      <WarningIcon sx={{ color: "#F59E0B", fontSize: 16 }} />
                    </Box>
                    <Typography sx={labelSx}>Warning / Aura</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomSelect
                      name="warning"
                      options={warningOptions}
                      value={formData.warning}
                      onChange={handleChange}
                      placeholder="Select"
                      fullWidth
                      searchable
                      searchPlaceholder="Search warning..."
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#8B5CF6", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#8B5CF6", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#8B5CF6")}>
                      <PersonIcon sx={{ color: "#8B5CF6", fontSize: 16 }} />
                    </Box>
                    <Typography sx={labelSx}>How User Felt</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomInput
                      name="userFeeling"
                      value={formData.userFeeling}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#2469F0", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#2469F0", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#2469F0")}>
                      <DurationIcon
                        sx={{ color: "#2469F0", fontSize: 16 }}
                      />
                    </Box>
                    <Typography sx={labelSx}>During Seizure</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomSelect
                      name="duringSeizure"
                      options={duringOptions}
                      value={formData.duringSeizure}
                      onChange={handleChange}
                      placeholder="Select"
                      fullWidth
                      searchable
                      searchPlaceholder="Search during..."
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#10B981", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#10B981", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#10B981")}>
                      <DurationIcon
                        sx={{ color: "#10B981", fontSize: 16 }}
                      />
                    </Box>
                    <Typography sx={labelSx}>Length (Minutes)</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomInput
                      name="length"
                      type="number"
                      value={formData.length}
                      onChange={handleChange}
                      placeholder="Enter duration"
                      fullWidth
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#10B981", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#10B981", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#10B981")}>
                      <RecoveryIcon
                        sx={{ color: "#10B981", fontSize: 16 }}
                      />
                    </Box>
                    <Typography sx={labelSx}>After Seizure</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomSelect
                      name="afterSeizure"
                      options={afterOptions}
                      value={formData.afterSeizure}
                      onChange={handleChange}
                      placeholder="Select"
                      fullWidth
                      searchable
                      searchPlaceholder="Search after..."
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#10B981", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#10B981", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#10B981")}>
                      <RecoveryIcon
                        sx={{ color: "#10B981", fontSize: 16 }}
                      />
                    </Box>
                    <Typography sx={labelSx}>Recovery Period</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomInput
                      name="recovery"
                      value={formData.recovery}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#8B5CF6", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#8B5CF6", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#8B5CF6")}>
                      <InterventionIcon
                        sx={{ color: "#8B5CF6", fontSize: 16 }}
                      />
                    </Box>
                    <Typography sx={labelSx}>Staff Interventions</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomSelect
                      name="intervention"
                      options={interventionOptions}
                      value={formData.intervention}
                      onChange={handleChange}
                      placeholder="Select"
                      fullWidth
                      searchable
                      searchPlaceholder="Search intervention..."
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha("#8B5CF6", 0.4),
                      boxShadow: `0px 2px 8px ${alpha("#8B5CF6", 0.08)}`,
                    },
                  }}
                >
                  <Box sx={fieldHeaderSx}>
                    <Box sx={iconBoxSx("#8B5CF6")}>
                      <InterventionIcon
                        sx={{ color: "#8B5CF6", fontSize: 16 }}
                      />
                    </Box>
                    <Typography sx={labelSx}>Founder / Other</Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <CustomInput
                      name="founder"
                      value={formData.founder}
                      onChange={handleChange}
                      placeholder="Additional details"
                      fullWidth
                    />
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  ...cardSx,
                  flex: "1 1 100%",
                  mb: 2,
                  "&:hover": {
                    borderColor: alpha("#F59E0B", 0.4),
                    boxShadow: `0px 2px 8px ${alpha("#F59E0B", 0.08)}`,
                  },
                }}
              >
                <Box sx={fieldHeaderSx}>
                  <Box sx={iconBoxSx("#F59E0B")}>
                    <NotesIcon sx={{ color: "#F59E0B", fontSize: 16 }} />
                  </Box>
                  <Typography sx={labelSx}>Action Taken & Comments</Typography>
                </Box>
                <CustomTextArea
                  name="action"
                  value={formData.action}
                  onChange={handleChange}
                  minRows={2}
                  maxRows={4}
                  fullWidth
                  placeholder="Add any notes..."
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                  p: 1.75,
                  borderRadius: "10px",
                  border: "1px solid #EAECF0",
                  bgcolor: "#FFFFFF",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={manager}
                        onChange={(e) => setManager(e.target.checked)}
                        size="small"
                        sx={{
                          color: "#D0D5DD",
                          "&.Mui-checked": { color: "#2469F0" },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: "#344054",
                          fontWeight: 500,
                        }}
                      >
                        Share with Manager
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={handover}
                        onChange={(e) => setHandover(e.target.checked)}
                        size="small"
                        sx={{
                          color: "#D0D5DD",
                          "&.Mui-checked": { color: "#2469F0" },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: "#344054",
                          fontWeight: 500,
                        }}
                      >
                        Handover
                      </Typography>
                    }
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  pt: 2,
                  borderTop: "1px solid #EAECF0",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={seizurePostMutation.isLoading}
                  startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    borderRadius: "8px",
                    borderColor: "#D0D5DD",
                    color: "#344054",
                    px: 2.5,
                    height: 38,
                    "&:hover": {
                      bgcolor: "#F9FAFB",
                      borderColor: "#98A2B3",
                    },
                  }}
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={seizurePostMutation.isLoading}
                  startIcon={
                    seizurePostMutation.isLoading ? (
                      <CircularProgress
                        size={16}
                        sx={{ color: "#FFFFFF" }}
                      />
                    ) : (
                      <SaveIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    borderRadius: "8px",
                    bgcolor: "#2469F0",
                    height: 38,
                    px: 3,
                    boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
                    "&:hover": {
                      bgcolor: "#1A5BD4",
                      boxShadow: "0px 2px 6px rgba(36, 105, 240, 0.25)",
                    },
                  }}
                >
                  {seizurePostMutation.isLoading
                    ? "Saving..."
                    : "Save Record"}
                </Button>
              </Box>
            </form>
          </CardContent>
        )}
      </Card>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={() => {
          seizureDeleteMutation.mutate(selectedSeizure);
        }}
        title="Delete Seizure Record?"
        body="Are you sure? This action cannot be undone."
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {selectedSeizure && (
        <EditSeizureModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          selectedSeizure={selectedSeizure}
          refetch={refetch}
        />
      )}
    </Box>
  );
};

export default SeizureChart;